import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { type MapNode } from "../map/CampaignMap";
import { generateAct, DEFAULT_NODE_WEIGHTS } from "../map/MapGenerator";
import { MapRenderer } from "../map/MapRenderer";
import { ACTS, getAct } from "../data/acts";
import { enterNode } from "./NodeRouter";
import { TeamHubScene } from "./TeamHubScene";
import { AchievementsScene } from "./AchievementsScene";
import { getDifficultyConfig } from "../data/difficulty";

export class MapScene implements Scene {
  private renderer!: MapRenderer;

  constructor(private game: Game) {
    // Lazily generate the map for the current act the first time it's needed.
    if (!game.runState!.map) {
      const act = getAct(game.runState!.act);
      const weights = { ...DEFAULT_NODE_WEIGHTS, ...act.nodeWeights };
      game.runState!.map = generateAct(game.runState!.act, game.runState!.seed, weights);
    }
  }

  enter(): void {
    this.game.saveRun();
    const state = this.game.runState!;
    const act = getAct(state.act);
    const subline = `Layer ${this.currentLayerNumber()} of 8 · Roster ${state.roster.length}/5`;
    const actLabel = `ACT ${state.act}/${ACTS.length} · ${act.name.toUpperCase()}`;
    const bgUrl = `${import.meta.env.BASE_URL}images/map/backgrounds/${act.region}.jpg`;
    this.renderer = new MapRenderer(state.map!, actLabel, subline, {
      onNodeClick: (node) => this.onNodeClick(node),
      onOpenHub: () => this.onOpenHub(),
      onOpenAchievements: () => this.onOpenAchievements(),
      onSlotSwap: (idA, idB) => {
        state.swapSlots(idA, idB);
        this.renderer.refreshRoster(state.roster);
      },
    }, state.roster, bgUrl, act.region, getDifficultyConfig(state.difficulty).themeClass);
    this.renderer.mount(this.game.sceneRoot);
  }

  exit(): void {
    this.renderer.unmount();
  }

  update(_dt: number): void {}
  render(_ctx: CanvasRenderingContext2D): void {}

  private onNodeClick(node: MapNode): void {
    const state = this.game.runState;
    if (!state?.map) return;
    state.map.advance(node.id);
    enterNode(this.game, node);
  }

  private onOpenHub(): void {
    this.game.sceneManager.transition(new TeamHubScene(this.game));
  }

  private onOpenAchievements(): void {
    this.game.sceneManager.transition(
      new AchievementsScene(this.game, () => {
        this.game.sceneManager.transition(new MapScene(this.game));
      }),
    );
  }

  private currentLayerNumber(): number {
    const state = this.game.runState!;
    if (!state.map?.currentNodeId) return 1;
    const cur = state.map.getNode(state.map.currentNodeId);
    if (!cur) return 1;
    // Layer 0 = start node; first real layer (1) shows as "Layer 1".
    return Math.max(1, cur.layer);
  }
}
